import React from "react"
import clsx from "clsx"

import useResizeObserver from "@components/hooks/useResizeObserver"

import styles from "./ShiftContainer.module.sass"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactElement | null
}

export const ShiftContainer: React.FC<Props> = React.memo(
  ({ children, className, ...rest }) => {
    const rootRef = React.useRef<HTMLDivElement>(null)

    const rect = useResizeObserver(rootRef)

    const prevChildren = React.useRef<React.ReactNode>(null)

    const [withAnimation, enableAnimation] = React.useState(false)

    React.useEffect(() => {
      let timeout: NodeJS.Timeout | null = null

      return () => {
        // eslint-disable-next-line eqeqeq
        if (prevChildren.current != children) {
          enableAnimation(true)
          if (timeout) clearTimeout(timeout)

          timeout = setTimeout(() => {
            enableAnimation(false)
          }, 400)

          prevChildren.current = children
        }
      }
    }, [getChildrenKey(children)])

    return (
      <div
        className={clsx(
          styles.container,
          { [styles.withAnimation]: withAnimation },
          className
        )}
        style={{ height: withAnimation ? rect?.height : "fit-content" }}
        {...rest}
      >
        {/* eslint-disable-next-line eqeqeq */}
        {withAnimation ? (
          <div className={styles.prevChildren} key="prev">
            {prevChildren.current}
          </div>
        ) : null}

        <div ref={rootRef} className={styles.blink} key="current">
          {children}
        </div>
      </div>
    )
  }
)

export default ShiftContainer

export const getChildrenKey = (children: React.ReactNode) => {
  if (React.isValidElement(children)) return children.key
  return null
}
